import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useIsMutating } from '@tanstack/react-query';
import { Pencil } from 'lucide-react';
import type { Task } from '@horizon/shared';
import { Button } from './ui/button';

const TaskEditor = lazy(() => import('./TaskEditor'));

export function TaskEditButton({ task }: { task: Task }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const busy = useIsMutating() > 0;
  const opened = useRef(false);
  useEffect(() => {
    if (open) opened.current = true;
    else if (opened.current && !busy) {
      button.current?.focus();
      opened.current = false;
    }
  }, [open, busy]);
  return (
    <>
      <Button
        ref={button}
        size="icon"
        variant="ghost"
        disabled={busy}
        aria-label={t('editor.editTask', { title: task.title })}
        onClick={() => setOpen(true)}
      >
        <Pencil size={16} aria-hidden="true" />
      </Button>
      {open &&
        createPortal(
          <Suspense fallback={<p role="status">{t('common.loading')}</p>}>
            <TaskEditor
              task={task}
              onClose={() => {
                setOpen(false);
              }}
            />
          </Suspense>,
          document.body,
        )}
    </>
  );
}
